import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import {
  BASE_API_URL,
  IListing,
  IListingPoint,
  ISearchQuery,
  ISubPhoto,
  LatLon,
  ListingPoint,
  SEARCH_API_URL,
  SearchResult,
} from '@curbnturf/entities';
import { isNumber } from '@curbnturf/helpers';
import {
  CachedHttpClient,
  DataCacheDbSet,
  ListingDbSet,
  ListingPhoto,
  ListingPhotosDbSet,
  Logger,
} from '@curbnturf/network';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

// @todo move this into the ListingSearch Service...if it's still useful.

@Injectable({
  providedIn: 'root',
})
export class ListingService {
  constructor(
    private http: CachedHttpClient,
    private dataCache: DataCacheDbSet,
    private listingDbSet: ListingDbSet,
    private listingPhotosDbSet: ListingPhotosDbSet,
    private logger: Logger,
    @Inject(PLATFORM_ID) private platformId: string,
    private transferState: TransferState,
  ) {}

  find(query: ISearchQuery, limit?: number, page?: number): Observable<SearchResult[]> {
    let queryString = `${SEARCH_API_URL}?${query.toString()}`;
    if (limit) queryString += `&limit=${limit}`;
    if (page) queryString += `&page=${limit}`;
    return this.http
      .get(queryString, undefined, {
        offlineCallback: () => [],
      })
      .pipe(
        map((data) => {
          return data.map(
            (mapPoint: IListingPoint) =>
              new SearchResult({
                type: 'listing',
                name: mapPoint.name,
                coordinates: mapPoint.coordinates,
                poi: undefined,
                mapPoint,
              }),
          );
        }),
      );
  }

  fetch(listingId: number | string): Observable<IListing> {
    const listingStateKey = makeStateKey<IListing>('listing-' + listingId);

    if (this.transferState.hasKey(listingStateKey)) {
      const listing = this.transferState.get<IListing>(listingStateKey, {});
      this.transferState.remove(listingStateKey);
      return of(listing);
    } else {
      return this.http
        .get<IListing>(`${BASE_API_URL}listing/${listingId}`, undefined, {
          offlineCallback: () => ({}), // @todo cache the boondock and load from the cache
        })
        .pipe(
          tap((listing) => {
            if (isPlatformServer(this.platformId)) {
              this.transferState.set(listingStateKey, listing);
            }
          }),
        );
    }
  }

  public async preload(): Promise<void> {
    const queryString = `${SEARCH_API_URL}`;

    this.logger.debug('Preloading Listing Data');

    try {
      this.http
        .get<IListingPoint[]>(queryString, undefined, {
          offlineCallback: () => [],
        })
        .subscribe({
          next: async (results: IListingPoint[]) => {
            const listingRecords: IListingPoint[] = [];
            const listingPhotoRecords: ListingPhoto[] = [];

            results.forEach((mapPoint: IListingPoint) => {
              if (mapPoint) {
                listingRecords.push(
                  new ListingPoint({
                    ...mapPoint,
                  }),
                );

                if (mapPoint.photos && Array.isArray(mapPoint.photos)) {
                  const photos: ISubPhoto[] = mapPoint.photos;
                  listingPhotoRecords.push(
                    ...photos.map((el: ISubPhoto) => ({
                      listingId: mapPoint?.id || 0,
                      key: el.key,
                      caption: el.caption || '',
                    })),
                  );
                }
              }
            });

            // Save the set
            await this.listingDbSet.truncateAndStoreArray(listingRecords);
            await this.listingPhotosDbSet.truncateAndStoreArray(listingPhotoRecords);

            await this.dataCache.store({
              cacheName: 'listings',
              lastModified: DateTime.now().toMillis(),
            });
          },
          error: (e) => {
            this.logger.error('Failed to Preload Listing Data', JSON.stringify(e));
          },
        });
    } catch (e) {
      this.logger.error('Failed to Preload Listing Data', JSON.stringify(e));
    }
  }

  async retrieveCachedListings(
    query: ISearchQuery,
    currentLocation: LatLon,
    take?: number,
    skip?: number,
  ): Promise<IListingPoint[]> {
    const results = await this.listingDbSet.retrieveFiltered(query, take, skip);

    // calculate and sort by distance from current location.
    results.sort((a, b) => {
      const aDistance = a.distance
        ? a.distance
        : currentLocation.distanceTo(new LatLon({ lat: a.coordinates.lat, lon: a.coordinates.lon }));
      a.distance = aDistance;
      const bDistance = b.distance
        ? b.distance
        : currentLocation.distanceTo(new LatLon({ lat: b.coordinates.lat, lon: b.coordinates.lon }));
      b.distance = bDistance;
      return aDistance - bDistance;
    });

    // Get the photo records
    const photoResults = await this.listingPhotosDbSet.retrieveBatch(
      results.map((el) => el.id).filter((id) => isNumber(id)) as number[],
    );
    results.forEach((listing) => {
      listing.photos = [...photoResults.filter((photo) => photo.listingId === listing.id)];
    });

    return results;
  }
}
